import React, { useState, useRef, useEffect, useLayoutEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../button/Button';
import { MoreVertical } from 'lucide-react';

export interface DropdownMenuProps {
  trigger?: ReactNode;
  children: ReactNode;
  className?: string;
  menuClassName?: string;
  align?: 'left' | 'right';
  width?: string;
  onOpenChange?: (open: boolean) => void;
}

export interface DropdownMenuItemProps {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  destructive?: boolean;
  className?: string;
}

export interface DropdownMenuSeparatorProps {
  className?: string;
}

export function DropdownMenu({
  trigger,
  children,
  className = '',
  menuClassName = '',
  align = 'right',
  width = 'w-48',
  onOpenChange
}: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{top: number, left: number} | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) &&
          triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
        handleOpenChange(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside, { passive: true });
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  // Calculate position
  useLayoutEffect(() => {
    if (isOpen && triggerRef.current && menuRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      let top = triggerRect.bottom + 4;
      
      // Check if it should drop up
      if (top + menuRect.height > viewportHeight && triggerRect.top > menuRect.height + 4) {
        top = triggerRect.top - menuRect.height - 4;
      }

      const left = align === 'right' 
        ? triggerRect.right - menuRect.width 
        : triggerRect.left;

      setPosition({ top, left });
    } else if (!isOpen) {
      setPosition(null);
    }
  }, [isOpen, align]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  const defaultTrigger = (
    <Button
      variant="ghost"
      size="sm"
      className="h-8 w-8 p-0"
    >
      <MoreVertical className="h-4 w-4" />
    </Button>
  );

  return (
    <div className={`relative ${className}`} ref={triggerRef}>
      <div
        onClick={(e) => {
          e.stopPropagation();
          handleOpenChange(!isOpen);
        }}
      >
        {trigger || defaultTrigger}
      </div>
      
      {isOpen && createPortal(
        <div 
          ref={menuRef}
          className={`fixed ${width} bg-popover text-popover-foreground border border-border rounded-md shadow-md z-50 ${menuClassName}`}
          style={{
            opacity: position ? 1 : 0,
            top: `${position?.top ?? 0}px`,
            left: `${position?.left ?? 0}px`,
            minWidth: `192px`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="py-1">
            {React.Children.map(children, (child) => {
              if (React.isValidElement(child)) {
                return React.cloneElement(child, {
                  // @ts-ignore
                  onItemClick: () => handleOpenChange(false)
                });
              }
              return child;
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export function DropdownMenuItem({
  children,
  onClick,
  disabled = false,
  destructive = false,
  className = '',
  onItemClick
}: DropdownMenuItemProps & { onItemClick?: () => void }) {
  const handleClick = () => {
    if (disabled) return;
    onClick?.();
    onItemClick?.();
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      className={`flex items-center w-full px-4 py-2 text-sm text-left transition-colors ${
        disabled
          ? 'text-muted-foreground cursor-not-allowed'
          : destructive
          ? 'text-destructive hover:bg-destructive/10 focus:bg-destructive/10'
          : 'text-popover-foreground hover:bg-muted focus:bg-muted'
      } ${className}`}
    >
      {children}
    </button>
  );
}

export function DropdownMenuSeparator({ className = '' }: DropdownMenuSeparatorProps) {
  return (
    <div className={`border-t border-border my-1 ${className}`} />
  );
}

// Export all components as a compound component
DropdownMenu.Item = DropdownMenuItem;
DropdownMenu.Separator = DropdownMenuSeparator;